// src/pages/Home.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HiArrowRight,
  HiX,
  HiAcademicCap,
  HiDocumentText,
  HiBookOpen,
  HiLightningBolt,
  HiCheckCircle,
  HiCollection,
  HiEye,
  HiOutlineArrowNarrowRight,
  HiSparkles,
  HiChat,
  HiSpeakerphone,
  HiCalculator,
  HiPencil,
  HiBeaker,
  HiChartBar,
  HiStar,
  HiClipboardList,
} from "react-icons/hi";
import { FaFacebook, FaYoutube, FaTwitter, FaLinkedinIn, FaWhatsapp, FaGlobe } from "react-icons/fa";
import { SEO } from "../components/common/SEO";
import Loader from "../components/common/Loader";
import { db } from "../components/firebase/firebase";
import { collection, getDocs, addDoc, serverTimestamp } from "firebase/firestore";

/* ─── Data ───────────────────────────────────────────────────────────── */

const BOARDS = [
  {
    id: "state",
    name: "State Board",
    subtitle: "Tamil Nadu State Board",
    gradient: "from-blue-500 to-blue-700",
    lightBg: "bg-blue-50",
    textColor: "text-blue-700",
    ringColor: "ring-blue-300",
    icon: <HiAcademicCap className="text-4xl text-white" />,
    subjects: [
      { label: "Mathematics", icon: <HiCalculator className="text-xs" /> },
      { label: "Science", icon: <HiBeaker className="text-xs" /> },
      { label: "English", icon: <HiBookOpen className="text-xs" /> },
      { label: "Tamil", icon: <HiPencil className="text-xs" /> },
      { label: "Social Science", icon: <HiChartBar className="text-xs" /> },
    ],
    classes: "Classes 1 – 12",
    papers: "500+",
    badge: "Most Popular",
  },
  {
    id: "cbse",
    name: "CBSE",
    subtitle: "Central Board of Secondary Education",
    gradient: "from-indigo-500 to-indigo-700",
    lightBg: "bg-indigo-50",
    textColor: "text-indigo-700",
    ringColor: "ring-indigo-300",
    icon: <HiBookOpen className="text-4xl text-white" />,
    subjects: [
      { label: "Mathematics", icon: <HiCalculator className="text-xs" /> },
      { label: "Physics", icon: <HiLightningBolt className="text-xs" /> },
      { label: "Chemistry", icon: <HiBeaker className="text-xs" /> },
      { label: "Biology", icon: <HiStar className="text-xs" /> },
      { label: "English", icon: <HiBookOpen className="text-xs" /> },
    ],
    classes: "Classes 6 – 12",
    papers: "300+",
    badge: null,
  },
  {
    id: "competitive",
    name: "Competitive",
    subtitle: "JEE · NEET · TNPSC & More",
    gradient: "from-sky-500 to-sky-700",
    lightBg: "bg-sky-50",
    textColor: "text-sky-700",
    ringColor: "ring-sky-300",
    icon: <HiLightningBolt className="text-4xl text-white" />,
    subjects: [
      { label: "Mathematics", icon: <HiCalculator className="text-xs" /> },
      { label: "Physics", icon: <HiLightningBolt className="text-xs" /> },
      { label: "Chemistry", icon: <HiBeaker className="text-xs" /> },
      { label: "Biology", icon: <HiStar className="text-xs" /> },
      { label: "Aptitude", icon: <HiClipboardList className="text-xs" /> },
    ],
    classes: "All Levels",
    papers: "200+",
    badge: "Entrance Exams",
  },
];


const FEATURES = [
  {
    icon: <HiCheckCircle className="text-2xl text-blue-600" />,
    bg: "bg-blue-50",
    title: "Curriculum Aligned",
    desc: "Every paper is mapped to the official syllabus — no guesswork.",
  },
  {
    icon: <HiCollection className="text-2xl text-indigo-600" />,
    bg: "bg-indigo-50",
    title: "All Boards, One Place",
    desc: "State, CBSE, and competitive — all at your fingertips.",
  },
  {
    icon: <HiEye className="text-2xl text-sky-600" />,
    bg: "bg-sky-50",
    title: "Instant Access",
    desc: "No sign-ups, no paywalls. Open any paper in seconds.",
  },
  {
    icon: <HiLightningBolt className="text-2xl text-violet-600" />,
    bg: "bg-violet-50",
    title: "Regularly Updated",
    desc: "Fresh papers added every term so you're always exam-ready.",
  },
];

const STEPS = [
  { num: "01", title: "Choose Your Board", desc: "Pick from State, CBSE or Competitive boards." },
  { num: "02", title: "Select Your Class", desc: "Navigate to your grade with a single tap." },
  { num: "03", title: "View & Practice", desc: "Open any paper instantly and practise — free." },
];

/* ─── Animation variants ─────────────────────────────────────────────── */

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -24 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

/* ─── FloatingBlob helper ────────────────────────────────────────────── */
function FloatingBlob({ className, delay = 0, duration = 7 }) {
  return (
    <motion.div
      className={`absolute rounded-full pointer-events-none ${className}`}
      animate={{ y: [0, -22, 0], x: [0, 12, 0], scale: [1, 1.06, 1] }}
      transition={{ duration, repeat: Infinity, delay, ease: "easeInOut" }}
    />
  );
}

/* ─── MathSymbol helper ──────────────────────────────────────────────── */
function MathSymbol({ symbol, className, duration = 10 }) {
  return (
    <motion.div
      className={`absolute text-white/15 font-bold select-none pointer-events-none ${className}`}
      animate={{ y: [0, -12, 0], rotate: [0, 5, 0] }}
      transition={{ duration, repeat: Infinity, ease: "easeInOut" }}
    >
      {symbol}
    </motion.div>
  );
}

/* ─── NewsTicker helper ──────────────────────────────────────────────── */
function NewsTicker({ news }) {
  const fmt = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const hasItems = news.length > 0;
  const shouldScroll = news.length > 2;
  // Duplicate the list so the vertical translate (-50%) loops seamlessly.
  const items = shouldScroll ? [...news, ...news] : news;

  const renderCard = (n, key) => (
    <div
      key={key}
      className="mb-3 rounded-2xl border border-blue-100 bg-blue-50/60 hover:bg-blue-50 hover:border-blue-200 p-4 transition-colors"
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="inline-flex items-center gap-1.5 text-blue-600 text-[10px] font-bold uppercase tracking-wider">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500" /> News
        </span>
        <span className="text-gray-400 text-[11px] whitespace-nowrap shrink-0">{fmt(n.createdAt)}</span>
      </div>
      <h4 className="text-gray-900 font-bold text-sm leading-snug">{n.title}</h4>
      <p className="text-gray-600 text-xs mt-1 leading-relaxed">{n.message}</p>
      {n.link && (
        <a
          href={n.link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs font-bold mt-2.5"
        >
          Read more <HiOutlineArrowNarrowRight />
        </a>
      )}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, x: -40 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.65, delay: 0.3 }}
      className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl shadow-blue-900/30"
    >
      {/* Card header — mirrors the Join Premium card */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-2xl bg-blue-600 flex items-center justify-center shadow-md flex-shrink-0">
          <HiSpeakerphone className="text-white text-xl" />
        </div>
        <div className="min-w-0">
          <h3 className="text-gray-900 font-black text-base leading-tight">Latest News &amp; Updates</h3>
          <p className="text-blue-600 text-xs font-semibold mt-0.5">Announcements &middot; Fresh every week</p>
        </div>
      </div>

      {!hasItems ? (
        <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50/50 py-10 px-4 text-center">
          <p className="text-gray-500 text-sm font-medium">No announcements right now.</p>
          <p className="text-gray-400 text-xs mt-1">Check back soon for updates!</p>
        </div>
      ) : shouldScroll ? (
        <div
          className="relative h-64 overflow-hidden"
          style={{
            maskImage: "linear-gradient(to bottom, transparent, black 8%, black 92%, transparent)",
            WebkitMaskImage: "linear-gradient(to bottom, transparent, black 8%, black 92%, transparent)",
          }}
        >
          <motion.div
            className="flex flex-col"
            animate={{ y: ["0%", "-50%"] }}
            transition={{ duration: news.length * 4, repeat: Infinity, ease: "linear" }}
          >
            {items.map((n, i) => renderCard(n, `${n.id}-${i}`))}
          </motion.div>
        </div>
      ) : (
        <div className="flex flex-col">
          {news.map((n) => renderCard(n, n.id))}
        </div>
      )}
    </motion.div>
  );
}

/* ─── Main component ─────────────────────────────────────────────────── */
function Home() {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [grades, setGrades] = useState([]);
  const trackRef = useRef(null);
  const boardSectionRef = useRef(null);
  const navigate = useNavigate();

  const [ads, setAds] = useState([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [showAdPopup, setShowAdPopup] = useState(true);
  const [forms, setForms] = useState([]);
  const [news, setNews] = useState([]);


  /* ── Firestore fetches ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!selectedBoard) { setGrades([]); return; }
    getDocs(collection(db, "curriculum", selectedBoard.id, "grades"))
      .then((snap) =>
        setGrades(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.order || 0) - (b.order || 0)))
      ).catch(console.error);
  }, [selectedBoard]);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "resources"));
        setPapers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "forms"));
        setForms(
          snap.docs.map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => {
              const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
              const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
              return tb - ta;
            })
        );
      } catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "news"));
        setNews(
          snap.docs.map((d) => ({ id: d.id, ...d.data() }))
            .filter((n) => n.visible !== false)
            .sort((a, b) => {
              const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
              const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
              return tb - ta;
            })
        );
      } catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "ads"));
        setAds(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(() => 0.5 - Math.random()));
      } catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => {
    if (ads.length > 1 && showAdPopup) {
      const t = setInterval(() => setCurrentAdIndex((p) => (p + 1) % ads.length), 2000);
      return () => clearInterval(t);
    }
  }, [ads, showAdPopup]);

  // WhatsApp group lead form
  const [waForm, setWaForm] = useState({ name: "", whoFilling: "", phone: "" });
  const [waSubmitting, setWaSubmitting] = useState(false);
  const [waSubmitted, setWaSubmitted] = useState(false);

  if (loading) return <Loader />;

  const activeAd = ads.length > 0 ? ads[currentAdIndex] : null;

  const formatDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };

  const scrollToBoards = () => boardSectionRef.current?.scrollIntoView({ behavior: "smooth" });

  const handleWASubmit = async () => {
    if (!waForm.phone.trim()) return;
    setWaSubmitting(true);
    try {
      await addDoc(collection(db, "whatsappLeads"), {
        name: waForm.name.trim() || null,
        whoFilling: waForm.whoFilling || null,
        phone: waForm.phone.trim(),
        createdAt: serverTimestamp(),
      });
      setWaSubmitted(true);
    } catch (e) {
      console.error(e);
    } finally {
      setWaSubmitting(false);
    }
  };

  return (
    <div className="overflow-x-hidden bg-white">
      <SEO
        title="Free Tamil Nadu Question Papers — State Board, CBSE &amp; Competitive"
        description="Free question papers for Tamil Nadu State Board, CBSE, JEE, NEET &amp; TNPSC — Classes 1 to 12, all subjects, view online. Updated every term. Free forever."
        canonical="/"
      />

      {/* ══════════════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 text-white">

        {/* Decorative blobs */}
        <FloatingBlob className="w-72 h-72 bg-white/10 blur-3xl -top-16 -left-16" delay={0} duration={8} />
        <FloatingBlob className="w-96 h-96 bg-indigo-400/20 blur-3xl -bottom-24 -right-24" delay={2} duration={10} />
        <FloatingBlob className="w-48 h-48 bg-sky-300/20 blur-2xl top-1/3 left-1/4" delay={1} duration={7} />

        {/* Dot grid overlay */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />

        {/* Floating math symbols */}
        <MathSymbol symbol="π"  className="text-6xl top-12  left-8"        duration={8.2} />
        <MathSymbol symbol="∑"  className="text-5xl top-20  right-16"      duration={9.7} />
        <MathSymbol symbol="√"  className="text-7xl bottom-24 left-20"     duration={11.1} />
        <MathSymbol symbol="∫"  className="text-6xl top-16  left-1/3"      duration={7.3} />
        <MathSymbol symbol="∞"  className="text-5xl bottom-16 right-24"    duration={10.8} />
        <MathSymbol symbol="x²" className="text-4xl top-32  right-1/3"     duration={8.9} />
        <MathSymbol symbol="÷"  className="text-5xl bottom-32 left-1/2"    duration={12.5} />
        <MathSymbol symbol="="  className="text-6xl top-10  right-8"       duration={9.1} />
        <MathSymbol symbol="Δ"  className="text-5xl top-1/2 left-4"        duration={13.4} />
        <MathSymbol symbol="θ"  className="text-4xl bottom-8 left-1/3"     duration={10.2} />
        <MathSymbol symbol="α"  className="text-5xl top-24  right-1/2"     duration={11.7} />
        <MathSymbol symbol="λ"  className="text-4xl bottom-20 right-1/3"   duration={9.4} />
        <MathSymbol symbol="σ"  className="text-5xl top-1/2 right-6"       duration={14.1} />
        <MathSymbol symbol="μ"  className="text-3xl top-8   left-1/2"      duration={12.0} />
        <MathSymbol symbol="×"  className="text-5xl bottom-10 left-8"      duration={8.6} />
        <MathSymbol symbol="φ"  className="text-4xl top-36  left-12"       duration={15.3} />

        <div className="relative mx-auto max-w-6xl px-6 py-16 sm:py-24">

          {/* ── Top band: headline + CTAs + social ──────────── */}
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <motion.div
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-white/15 border border-white/30 backdrop-blur-sm text-white text-xs font-semibold tracking-widest uppercase px-5 py-2 rounded-full mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              India's Premier Test Paper Platform
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mb-6"
            >
              Ace Your Exams with{" "}
              <span className="relative inline-block">
                <span className="text-yellow-300">Ravi TestPapers</span>
                <motion.span
                  className="absolute -bottom-1 left-0 right-0 h-1 bg-yellow-300 rounded-full"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  style={{ originX: 0 }}
                />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="text-blue-100 text-base sm:text-lg leading-relaxed mb-10"
            >
              Thousands of curated question papers for State, CBSE, and Competitive exams —
              organised by grade, subject and term. Free forever.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.38 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <button
                onClick={scrollToBoards}
                className="inline-flex items-center gap-2 bg-white text-blue-700 font-bold px-8 py-3.5 rounded-full text-sm hover:bg-blue-50 transition-all duration-200 shadow-lg shadow-blue-900/30"
              >
                Browse Papers <HiArrowRight />
              </button>
              <a
                href="/get-help"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-bold px-8 py-3.5 rounded-full text-sm transition-all duration-200 shadow-lg shadow-yellow-500/30"
              >
                <HiSparkles /> Get Personalised Help
              </a>
            </motion.div>

            {/* ── Social links ───────────────────────────────── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.52 }}
              className="flex items-center justify-center gap-3 mt-6"
            >
              <span className="text-blue-200 text-xs font-semibold tracking-wide uppercase mr-1">Follow us</span>
              {[
                { icon: <FaFacebook />, href: "https://www.facebook.com/groups/2539806196195532/?ref=share&mibextid=NSMWBT", label: "Facebook", hover: "hover:bg-blue-600" },
                { icon: <FaYoutube />, href: "https://www.youtube.com/@ravitestpapers", label: "YouTube", hover: "hover:bg-red-600" },
                { icon: <FaTwitter />, href: "https://x.com/Ravimathssir", label: "X (Twitter)", hover: "hover:bg-gray-900" },
                { icon: <FaLinkedinIn />, href: "https://www.linkedin.com/in/ravi-s-093838310", label: "LinkedIn", hover: "hover:bg-blue-700" },
                { icon: <FaWhatsapp />, href: "https://wa.me/918056206308", label: "WhatsApp", hover: "hover:bg-green-500" },
                { icon: <FaGlobe />, href: "https://www.ravitestpapers.in/", label: "Website", hover: "hover:bg-sky-500" },
              ].map(({ icon, href, label, hover }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className={`w-9 h-9 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-white text-base transition-all duration-200 ${hover} hover:border-transparent hover:scale-110 hover:shadow-lg`}
                >
                  {icon}
                </a>
              ))}
            </motion.div>
          </div>

          {/* ── Two columns: News (left) + Join card (right) ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

          {/* ── Left: News ticker ── */}
          <NewsTicker news={news} />

          {/* ── Right: WhatsApp join card ───────────────────── */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.65, delay: 0.3 }}
            className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl shadow-blue-900/30"
          >
            {waSubmitted ? (
              <div className="text-center py-8">
                <div className="text-5xl mb-4">✅</div>
                <h3 className="text-xl font-black text-gray-900 mb-2">You're on the list!</h3>
                <p className="text-gray-500 text-sm">We'll get in touch with you on the WhatsApp shortly.</p>
              </div>
            ) : (
              <>
                {/* Card header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-2xl bg-green-500 flex items-center justify-center shadow-md flex-shrink-0">
                    <HiChat className="text-white text-xl" />
                  </div>
                  <div>
                    <h3 className="text-gray-900 font-black text-base leading-tight">Join Premium WhatsApp Group</h3>
                    <p className="text-green-600 text-xs font-semibold mt-0.5">Paid access · Exclusive content</p>
                  </div>
                </div>

                {/* Benefits */}
                <ul className="space-y-2 mb-5 bg-green-50 rounded-2xl p-4">
                  {[
                    "1000+ exclusive question papers",
                    "All boards · All classes · All subjects",
                    "New papers added every week",
                    "Direct access to expert teachers",
                  ].map((b) => (
                    <li key={b} className="flex items-center gap-2 text-gray-700 text-xs">
                      <span className="w-4 h-4 rounded-full bg-green-500 text-white flex items-center justify-center flex-shrink-0 text-[10px] font-bold">✓</span>
                      {b}
                    </li>
                  ))}
                </ul>

                {/* Form */}
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Your name (optional)"
                    value={waForm.name}
                    onChange={(e) => setWaForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-300 focus:border-green-300"
                  />
                  <select
                    value={waForm.whoFilling}
                    onChange={(e) => setWaForm((f) => ({ ...f, whoFilling: e.target.value }))}
                    className="w-full bg-gray-50 border border-gray-200 text-gray-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-300 focus:border-green-300"
                  >
                    <option value="">I am a…</option>
                    <option>Student</option>
                    <option>Parent</option>
                    <option>Teacher</option>
                    <option>Academy Owner</option>
                  </select>
                  <input
                    type="tel"
                    placeholder="WhatsApp number *"
                    value={waForm.phone}
                    onChange={(e) => setWaForm((f) => ({ ...f, phone: e.target.value }))}
                    className="w-full bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-300 focus:border-green-300"
                  />
                  <button
                    onClick={handleWASubmit}
                    disabled={waSubmitting || !waForm.phone.trim()}
                    className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold py-3 rounded-xl text-sm transition-colors duration-200 flex items-center justify-center gap-2 shadow-md shadow-green-500/30"
                  >
                    <HiChat className="text-base" />
                    {waSubmitting ? "Submitting…" : "Request to Join"}
                  </button>
                  <p className="text-gray-400 text-[11px] text-center leading-relaxed">
                    We'll reach out on WhatsApp to complete your enrolment.
                  </p>
                </div>
              </>
            )}
          </motion.div>

          </div>
        </div>

        {/* Wave bottom */}
        <div className="relative h-14 sm:h-20">
          <svg
            viewBox="0 0 1440 80"
            preserveAspectRatio="none"
            className="absolute inset-0 w-full h-full"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          BOARD / GRADE / EXAM PICKER
      ══════════════════════════════════════════════════════ */}
      <section ref={boardSectionRef} className="relative py-16 bg-gradient-to-b from-white via-slate-50 to-white overflow-hidden">

        {/* ── Notebook-paper faint lines ──────────────────── */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.035] pointer-events-none"
          style={{
            backgroundImage: "repeating-linear-gradient(transparent, transparent 31px, #6366f1 32px)",
          }}
        />

        {/* ── Decorative oversized doodles ────────────────── */}
        <HiCalculator className="absolute -left-6 top-10 text-[160px] text-slate-200 pointer-events-none select-none rotate-[-12deg]" aria-hidden />
        <HiBookOpen   className="absolute -right-6 bottom-10 text-[160px] text-slate-200 pointer-events-none select-none rotate-[10deg]" aria-hidden />
        <HiBeaker     className="absolute left-1/2 -top-8 text-[120px] text-slate-100 pointer-events-none select-none -translate-x-1/2" aria-hidden />

        <div className="relative mx-auto max-w-screen-lg px-6">

          {/* ── Step 1: Board Selection ────────────────────── */}
          {!selectedBoard && (
            <>
              {/* ── Step progress strip ── */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45 }}
                className="flex items-center justify-center gap-1 sm:gap-2 mb-8"
              >
                {[
                  { n: "1", label: "Choose Board", active: true },
                  { n: "2", label: "Pick Class",   active: false },
                  { n: "3", label: "View Paper",   active: false },
                ].map((s, i) => (
                  <React.Fragment key={s.n}>
                    <div className="flex items-center gap-1.5">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-colors ${s.active ? "bg-blue-600 text-white shadow-md shadow-blue-200" : "bg-gray-100 text-gray-400"}`}>
                        {s.n}
                      </div>
                      <span className={`text-xs font-semibold hidden sm:block ${s.active ? "text-blue-700" : "text-gray-400"}`}>{s.label}</span>
                    </div>
                    {i < 2 && <div className={`w-6 sm:w-10 h-0.5 mx-1 rounded-full ${i === 0 ? "bg-blue-200" : "bg-gray-100"}`} />}
                  </React.Fragment>
                ))}
              </motion.div>

              {/* Section header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-center mb-10"
              >
                <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-2">
                  Choose Your <span className="text-blue-600">Board</span>
                </h2>
                <p className="text-gray-500 text-sm max-w-md mx-auto">
                  Select your curriculum board — State, CBSE, or Competitive — to browse papers by class and subject.
                </p>
              </motion.div>

              {/* Cards */}
              <motion.div
                variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="grid grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-6 sm:gap-5"
              >
                {BOARDS.map((b) => {
                  const boardPapersCount = papers.filter((p) => p.type === b.id || p.board === b.id).length;
                  return (
                  <motion.button
                    key={b.id}
                    variants={fadeUp}
                    onClick={() => setSelectedBoard(b)}
                    whileHover={{ y: -8, boxShadow: "0 28px 56px rgba(0,0,0,0.11)" }}
                    whileTap={{ scale: 0.97 }}
                    className={`group relative bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-xl border border-gray-100 hover:border-transparent focus:outline-none focus:ring-2 ${b.ringColor} transition-all duration-300 text-left`}
                  >
                    {/* Gradient header */}
                    <div className={`relative bg-gradient-to-br ${b.gradient} p-6`}>
                      {b.badge && (
                        <span className="absolute top-3 right-3 bg-white/25 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
                          {b.badge}
                        </span>
                      )}
                      {/* Faint watermark symbol */}
                      <div className="absolute bottom-0 right-3 text-white/10 text-[80px] font-black leading-none select-none pointer-events-none">
                        {b.id === "state" ? "∑" : b.id === "cbse" ? "∫" : "∞"}
                      </div>
                      {/* Icon */}
                      <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                        {b.icon}
                      </div>
                      <h3 className="text-white font-black text-xl leading-tight">{b.name}</h3>
                      <p className="text-white/75 text-xs mt-1">{b.subtitle}</p>
                    </div>

                    {/* White body — curved top overlap */}
                    <div className="bg-white rounded-t-3xl -mt-3 px-5 pt-4 pb-5">
                      {/* Class range pill */}
                      <div className={`inline-flex items-center gap-1.5 ${b.lightBg} ${b.textColor} text-[11px] font-bold px-3 py-1.5 rounded-full mb-4`}>
                        <HiAcademicCap className="text-sm" /> {b.classes}
                      </div>

                      {/* Subject chips */}
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {b.subjects.map((s) => (
                          <span
                            key={s.label}
                            className="inline-flex items-center gap-1 bg-gray-50 border border-gray-100 text-gray-600 text-[11px] font-medium px-2.5 py-1 rounded-lg"
                          >
                            {s.icon} {s.label}
                          </span>
                        ))}
                      </div>

                      {/* Footer row */}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                        <span className="text-gray-400 text-xs font-medium">
                          <span className="text-gray-700 font-bold">{boardPapersCount > 0 ? boardPapersCount + "+" : "0"}</span> papers
                        </span>
                        <div className={`flex items-center gap-1.5 text-xs font-bold bg-gradient-to-r ${b.gradient} text-white px-4 py-1.5 rounded-full group-hover:scale-105 transition-transform duration-200 shadow-sm`}>
                          Browse <HiArrowRight />
                        </div>
                      </div>
                    </div>
                  </motion.button>
                  );
                })}
              </motion.div>

              {/* Info chips below cards */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="flex flex-wrap items-center justify-center gap-3 mt-8"
              >
                {[
                  { icon: <HiEye />, text: "Free to View" },
                  { icon: <HiCheckCircle />, text: "Syllabus Aligned" },
                  { icon: <HiCollection />, text: "1000+ Papers" },
                  { icon: <HiSparkles />, text: "Updated Every Term" },
                ].map((c) => (
                  <span key={c.text} className="inline-flex items-center gap-1.5 bg-white border border-gray-100 shadow-sm text-gray-600 text-xs font-semibold px-4 py-2 rounded-full">
                    <span className="text-blue-500">{c.icon}</span> {c.text}
                  </span>
                ))}
              </motion.div>
            </>
          )}

          {/* ── Step 2: Grade Selection ─────────────────────── */}
          {selectedBoard && (
            <>
              {/* Step progress */}
              <div className="flex items-center justify-center gap-1 sm:gap-2 mb-8">
                {[
                  { n: "1", label: "Choose Board", active: false, done: true },
                  { n: "2", label: "Pick Class",   active: true,  done: false },
                  { n: "3", label: "View Paper",   active: false, done: false },
                ].map((s, i) => (
                  <React.Fragment key={s.n}>
                    <div className="flex items-center gap-1.5">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-colors ${s.active ? "bg-blue-600 text-white shadow-md shadow-blue-200" : s.done ? "bg-green-500 text-white" : "bg-gray-100 text-gray-400"}`}>
                        {s.done ? "✓" : s.n}
                      </div>
                      <span className={`text-xs font-semibold hidden sm:block ${s.active ? "text-blue-700" : s.done ? "text-green-600" : "text-gray-400"}`}>{s.label}</span>
                    </div>
                    {i < 2 && <div className={`w-6 sm:w-10 h-0.5 mx-1 rounded-full ${s.done ? "bg-green-300" : "bg-gray-100"}`} />}
                  </React.Fragment>
                ))}
              </div>

              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="mb-10">
                <button
                  onClick={() => setSelectedBoard(null)}
                  className="inline-flex items-center gap-1 text-blue-600 text-sm font-semibold hover:text-blue-800 mb-6 transition-colors"
                >
                  ← Back to Boards
                </button>
                <div className="text-center">
                  <span className={`inline-block bg-gradient-to-r ${selectedBoard.gradient} text-white text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-3`}>
                    {selectedBoard.name}
                  </span>
                  <h2 className="text-3xl sm:text-4xl font-black text-gray-900">Choose Your Class</h2>
                </div>
              </motion.div>

              {grades.length === 0 ? (
                <p className="text-center text-gray-400 py-16">No classes set up for this board yet.</p>
              ) : (
                <motion.div
                  variants={stagger} initial="hidden" animate="visible"
                  className="grid grid-cols-4 md:grid-cols-3 sm:grid-cols-2 gap-4"
                >
                  {grades.map((g) => (
                    <motion.button
                      key={g.id}
                      variants={fadeUp}
                      onClick={() => navigate(`/practice/${selectedBoard.id}/${g.label}`)}
                      whileHover={{ y: -4, scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      className={`group relative bg-gradient-to-br ${selectedBoard.gradient} text-white rounded-2xl p-6 flex flex-col items-center justify-center gap-1 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-200`}
                    >
                      <span className="text-3xl font-black leading-none">{g.label}</span>
                      <span className="text-sm font-medium text-white/80">{g.name}</span>
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </>
          )}

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          LATEST PAPERS
      ══════════════════════════════════════════════════════ */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-screen-lg px-6">
          <motion.div
            variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="flex items-end justify-between mb-8"
          >
            <div>
              <motion.p variants={fadeLeft} className="text-blue-600 text-xs font-bold tracking-widest uppercase mb-2">
                Library
              </motion.p>
              <motion.h2 variants={fadeLeft} className="text-3xl font-black text-gray-900">
                Latest Papers
              </motion.h2>
            </div>
            <motion.div variants={fadeLeft}>
              <Link
                to="/practice"
                className="inline-flex items-center gap-2 text-blue-600 font-semibold text-sm hover:text-blue-800 transition-colors"
              >
                View All <HiArrowRight />
              </Link>
            </motion.div>
          </motion.div>

          <div
            ref={trackRef}
            className="flex gap-5 pb-4 overflow-x-auto snap-x snap-mandatory no-scrollbar"
          >
            {papers.slice(0, 12).map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 4) * 0.08 }}
                whileHover={{ y: -6 }}
                className="bg-white rounded-2xl border-2 border-gray-100 hover:border-blue-200 shadow-sm hover:shadow-lg flex-shrink-0 w-56 snap-start overflow-hidden transition-all duration-300"
              >
                <div className="p-4 flex flex-col gap-2">
                  <div className="flex items-center gap-1.5">
                    {p.subject && (
                      <span className="bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full capitalize">
                        {p.subject}
                      </span>
                    )}
                    {(p.month || p.year) && (
                      <span className="bg-gray-500 text-white text-[10px] font-medium px-2 py-0.5 rounded-full ml-auto">
                        {p.month ? p.month.slice(0, 3) : ""} {p.year}
                      </span>
                    )}
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2">{p.title}</h3>
                  {p.refId && (
                    <p className="text-[10px] font-mono text-gray-400 tracking-wider">#{p.refId}</p>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {(p.board || p.type) && (
                      <span className="bg-blue-50 text-blue-700 text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize border border-blue-100">
                        {{ state: "State", cbse: "CBSE", competitive: "Competitive" }[p.board || p.type] || (p.board || p.type)}
                      </span>
                    )}
                    {p.grade && (
                      <span className="bg-indigo-50 text-indigo-700 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-indigo-100">
                        Class {p.grade}
                      </span>
                    )}
                  </div>
                  <Link
                    to={`/paper/${p.id}`}
                    className="inline-flex items-center gap-1.5 text-blue-600 font-semibold text-xs hover:text-blue-800 transition-colors mt-1"
                  >
                    View Paper <HiArrowRight />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          HOW IT WORKS
      ══════════════════════════════════════════════════════ */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-3xl px-6">
          <motion.div
            variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="text-center mb-14"
          >
            <motion.p variants={fadeUp} className="text-blue-600 text-xs font-semibold tracking-widest uppercase mb-3">
              Simple Process
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl font-bold text-gray-900">
              How It Works
            </motion.h2>
          </motion.div>

          <motion.div
            variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="relative flex items-start justify-between"
          >
            {/* Dotted connector line — sits at the vertical centre of the number circles */}
            <div
              aria-hidden
              className="absolute top-7 z-0"
              style={{
                left: "calc(100% / 6)",
                right: "calc(100% / 6)",
                borderTop: "2px dashed #BFDBFE",
              }}
            />

            {STEPS.map((s) => (
              <motion.div
                key={s.num}
                variants={fadeUp}
                className="relative z-10 flex flex-col items-center text-center flex-1 px-3"
              >
                {/* Circle */}
                <div className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center mb-4 ring-4 ring-white shadow-md shadow-blue-200">
                  <span className="text-base font-bold text-white">{s.num}</span>
                </div>
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">{s.title}</h3>
                <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          FEATURE HIGHLIGHTS
      ══════════════════════════════════════════════════════ */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-screen-lg px-6">
          <motion.div
            variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="text-center mb-12"
          >
            <motion.p variants={fadeUp} className="text-blue-600 text-xs font-bold tracking-widest uppercase mb-3">
              Why Choose Us
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-black text-gray-900">
              Built for Students, by Educators
            </motion.h2>
          </motion.div>

          <motion.div
            variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {FEATURES.map((f) => (
              <motion.div
                key={f.title}
                variants={fadeUp}
                whileHover={{ y: -6 }}
                className="bg-white rounded-2xl border-2 border-gray-100 hover:border-blue-100 hover:shadow-lg p-6 flex flex-col gap-4 transition-all duration-300"
              >
                <div className={`w-12 h-12 rounded-xl ${f.bg} flex items-center justify-center`}>
                  {f.icon}
                </div>
                <div>
                  <h3 className="font-black text-gray-900 mb-1">{f.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          FORMS & NOTICES
      ══════════════════════════════════════════════════════ */}
      {forms.length > 0 && (
        <section className="py-16 bg-blue-50">
          <div className="mx-auto max-w-screen-lg px-6">
            <motion.div
              variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
              className="mb-10"
            >
              <motion.p variants={fadeUp} className="text-blue-600 text-xs font-bold tracking-widest uppercase mb-3">
                Announcements
              </motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl font-black text-gray-900">
                Forms &amp; Notices
              </motion.h2>
            </motion.div>

            <motion.div
              variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
              className="grid grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-5"
            >
              {forms.map((f) => (
                <motion.a
                  key={f.id}
                  href={f.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  variants={fadeUp}
                  whileHover={{ y: -4 }}
                  className="bg-white rounded-2xl border-2 border-blue-100 hover:border-blue-300 hover:shadow-md p-6 flex flex-col justify-between transition-all duration-200"
                >
                  <div className="flex items-start gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <HiDocumentText className="text-blue-600 text-xl" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 leading-snug">
                        {f.title || "Untitled Form"}
                      </h3>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {formatDate(f.createdAt)}{f.note ? ` · ${f.note}` : ""}
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors">
                    Open Form <HiOutlineArrowNarrowRight />
                  </span>
                </motion.a>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════
          CTA BANNER
      ══════════════════════════════════════════════════════ */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-indigo-700 relative overflow-hidden">
        <FloatingBlob className="w-64 h-64 bg-white/10 blur-3xl -top-16 -right-16" delay={0} duration={9} />
        <motion.div
          variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="relative mx-auto max-w-2xl px-6 text-center"
        >
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-black text-white mb-4">
            Ready to Start Practising?
          </motion.h2>
          <motion.p variants={fadeUp} className="text-blue-200 text-base mb-8">
            Browse thousands of free question papers and build your exam confidence today.
          </motion.p>
          <motion.div variants={fadeUp}>
            <button
              onClick={scrollToBoards}
              className="inline-flex items-center gap-2 bg-white text-blue-700 font-black px-10 py-4 rounded-full text-base hover:bg-blue-50 transition-all shadow-lg shadow-blue-900/30"
            >
              Get Started <HiArrowRight />
            </button>
          </motion.div>
        </motion.div>
      </section>

      {/* ══════════════════════════════════════════════════════
          AD POPUP
      ══════════════════════════════════════════════════════ */}
      {showAdPopup && activeAd && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50"
          onClick={() => setShowAdPopup(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4 overflow-hidden relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowAdPopup(false)}
              className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
              aria-label="Close"
            >
              <HiX className="text-lg" />
            </button>

            {activeAd.imageURL && (
              <img
                src={activeAd.imageURL}
                alt={activeAd.title || "Advertisement"}
                className="w-full object-cover"
                style={{ minHeight: "280px", maxHeight: "460px" }}
              />
            )}
            {activeAd.title && (
              <div className="px-6 py-5 text-center bg-gradient-to-r from-blue-600 to-indigo-700">
                <h2 className="text-base font-bold text-white">{activeAd.title}</h2>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}

export { Home };
export default Home;
